import React from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Event as EventIcon,
  Group as GroupIcon,
  EnergySavingsLeaf as EcoIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Hero Section */}
      <Paper
        sx={{
          py: 10,
          px: 2,
          mb: 8,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "common.white",
          borderRadius: 0,
          boxShadow: theme.shadows[4],
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              ExpoPlanner
            </Typography>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                maxWidth: 700,
                mb: 4,
                opacity: 0.9,
              }}
            >
              The all-in-one event platform designed to create meaningful
              connections and unforgettable experiences
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: theme.shadows[4],
                "&:hover": {
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              Get Started
            </Button>
          </Box>
        </Container>
      </Paper>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 10, flex: 1 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: 600, mb: 6 }}
        >
          Powerful Features for Your Next Event
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.title}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                      borderRadius: "50%",
                      mb: 3,
                    }}
                  >
                    {React.cloneElement(feature.icon, {
                      sx: { fontSize: 40 },
                    })}
                  </Box>
                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 5,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="space-between">
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                ExpoPlanner
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Empowering event organizers with cutting-edge technology since
                2023.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Quick Links
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {footerLinks.map((link) => (
                  <RouterLink
                    key={link.to}
                    to={link.to}
                    style={{
                      textDecoration: "none",
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Typography variant="body2">{link.label}</Typography>
                  </RouterLink>
                ))}
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 2 }}
          >
            © {new Date().getFullYear()} ExpoPlanner. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

const features = [
  {
    title: "Smart Event App",
    description:
      "Our comprehensive platform offers seamless lead intelligence, customizable website builder, and real-time analytics to power your event success.",
    icon: <EventIcon />,
  },
  {
    title: "AI Matchmaking",
    description:
      "Leverage our advanced AI algorithms to spark high-value connections between attendees, maximizing engagement and ROI for all participants.",
    icon: <GroupIcon />,
  },
  {
    title: "Sustainability",
    description:
      "Our green initiative helps reduce your event's carbon footprint while maintaining exceptional attendee experiences through innovative solutions.",
    icon: <EcoIcon />,
  },
];

const footerLinks = [
  { to: "#", label: "Privacy Policy" },
  { to: "#", label: "Terms of Use" },
  { to: "#", label: "Contact Us" },
  { to: "#", label: "About Us" },
  { to: "#", label: "Features" },
  { to: "#", label: "Pricing" },
];

export default HomePage;
